import React, { useEffect, useState } from 'react'
import { useStaticQuery, graphql, Link } from 'gatsby'
import { Flex, Box, Heading, Text, Image, Button } from 'rebass/styled-components'
import { useTranslation } from 'gatsby-plugin-react-i18next'
import { get } from 'lodash'
import { useCookies } from 'react-cookie'

import theme from '../lib/theme'

const ARENA_OPTIN_COOKIE = 'arena.optin'

const Footer = () => {
  const [modalCookies, setModalCookies] = useState(false)
  const [cookies, setCookies, removeCookies] = useCookies([ARENA_OPTIN_COOKIE])
  const [activeOpt, setActiveOpt] = useState(cookies[ARENA_OPTIN_COOKIE])

  const { t } = useTranslation('footer')
  const copyrightLinks = t('copyright.links', { returnObjects: true })
  const columns = t('columns', { returnObjects: true })
  const images = useStaticQuery(graphql`
    query {
      logo: file(relativePath: { eq: "footer/a.svg" }) {
        publicURL
      }
      gdpr: file(relativePath: { eq: "footer/gdpr.png" }) {
        publicURL
      }
      privacy: file(relativePath: { eq: "footer/privacy.png" }) {
        publicURL
      }
    }
  `)

  const logo = get(images, 'logo.publicURL')
  const gdpr = get(images, 'gdpr.publicURL')
  const privacy = get(images, 'privacy.publicURL')

  const handleActiveOpt = () => {
    setActiveOpt(true)

    setModalCookies(false)
  }

  useEffect(() => {
    if (activeOpt) {
      setCookies(ARENA_OPTIN_COOKIE, true)
    } else {
      removeCookies(ARENA_OPTIN_COOKIE, false)
    }
  }, [activeOpt])

  useEffect(() => {
    if (activeOpt) {
      setModalCookies(false)
    }

    if (!activeOpt) {
      setTimeout(() => setModalCookies(true), 3000)
    }
  }, [])

  return (
    <Box as="footer" width={1}>
      <Flex
        alignItems="center"
        justifyContent="center"
        maxWidth={['calc(100% - 40px)', '560px']}
        width={1}
        height="60px"
        mx={[5, 0]}
        px={[5, 0]}
        bg="white"
        sx={{
          position: 'fixed',
          bottom: '32px',
          left: ['0', '40px'],
          zIndex: 15,
          borderRadius: '200px',
          boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
          transform: 'translateX(-130%)',
          transition: 'all .325s ease-in-out',

          '&': modalCookies && {
            transform: 'translateX(0)'
          }
        }}
      >
        <Text
          mr="48px"
          variant="level6"
          fontSize={theme.customFontsize(12, 14)}
          sx={{
            '& a': {
              color: 'purple'
            }
          }}
        >
          {t('cookies.text')}
          <span>
            <a href="/cookie-policy">cookie policy.</a>
          </span>
        </Text>

        <Button
          onClick={handleActiveOpt}
          height="36px"
          width="80px"
          sx={{
            fontSize: theme.customFontsize(12, 14)
          }}
        >
          {t('cookies.button')}
        </Button>
      </Flex>

      <Flex
        flexDirection={['column', 'column', 'row']}
        justifyContent="space-between"
        flexWrap="wrap"
        maxWidth="container"
        width={1}
        mx="auto"
        pt={[12, 12, 25]}
        px={[5, 5, 0]}
        pb={[0, 0, 12]}
      >
        <Box width="auto" mb={[15, 15, 0]} textAlign={['left', 'center', 'left']}>
          <Image src={logo} alt="Logo Arena" width="32px" />

          <Flex
            alignItems="center"
            justifyContent={['flex-start', 'center', 'flex-start']}
            mt={8}
            mb={8}
          >
            <Image src={gdpr} alt="Logo GDPR" mr={4} width="24px" />

            <Text
              variant="level3"
              fontSize="14px"
              lineHeight="150%"
              fontWeight="400"
              color="#435565"
            >
              {t('logo.gdpr')}
            </Text>
          </Flex>

          <Flex alignItems="center" justifyContent={['flex-start', 'center', 'flex-start']}>
            <Image src={privacy} alt="Logo Privacy" mr={4} width="24px" />

            <Text
              variant="level3"
              fontSize="14px"
              lineHeight="150%"
              fontWeight="400"
              color="#435565"
            >
              {t('logo.privacy')}
            </Text>
          </Flex>
        </Box>

        {Object.entries(columns).map(([keyTitle, valueTitle]) => (
          <Box key={keyTitle} mb={[15, 15, 0]} textAlign={['left', 'center', 'left']}>
            <Box mb={[8, 8, 10]}>
              <Heading variant="level3" fontSize="16px" lineHeight="150%">
                {valueTitle.title}
              </Heading>
            </Box>

            <Box mb={[-6, -8]}>
              {Object.entries(valueTitle.links).map(([key, value]) => (
                <Box key={key} mb={[6, 6, 8]}>
                  <Link to={`/${key}`}>
                    <Text variant="level3" fontSize="14px" fontWeight="400" color="darkGray">
                      {value}
                    </Text>
                  </Link>
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </Flex>

      <Box width={1}>
        <Flex
          flexDirection={['column-reverse', 'column-reverse', 'row']}
          alignItems={['flex-start', 'center', 'center']}
          justifyContent={['flex-start', 'center', 'space-between']}
          maxWidth="container"
          mx="auto"
          py={[0, 0, 12]}
          px={[5, 5, 0]}
          sx={{
            '@media (min-width: 80rem)': {
              borderTop: '0.5px solid #DEDEDE'
            }
          }}
        >
          <Box
            flexGrow="2"
            flexBasis="0"
            width={[1, 'auto', 'auto']}
            mx="auto"
            py={[8, 8, 0]}
            textAlign={['center', 'center', 'left']}
            sx={{
              '@media (max-width: 80rem)': {
                borderTop: '0.5px solid #DEDEDE'
              }
            }}
          >
            <Text variant="level3" fontSize="14px">
              © 2020 Arena.im
            </Text>
          </Box>

          <Flex
            flexDirection={['column', 'column', 'row']}
            alignItems={['flex-start', 'center', 'center']}
            justifyContent="space-between"
            width={['100%', 'auto', 'auto']}
            mb={[-3, -3, 0]}
            mr={[0, 0, -12]}
          >
            {Object.entries(copyrightLinks).map(([key, value]) => (
              <Box key={key} mr={[0, 0, 12]} mb={[10, 10, 0]}>
                <Link to={`/${key}`}>
                  <Text variant="level3" fontSize={14} fontWeight="400">
                    {value}
                  </Text>
                </Link>
              </Box>
            ))}
          </Flex>
        </Flex>
      </Box>
    </Box>
  )
}

export default Footer
